/* eslint-disable jsx-a11y/media-has-caption */
import React, {
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import {
  arrayOf,
  bool,
  func,
  node,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import videojs from 'video.js';

import 'videojs-seek-buttons';
import 'videojs-vjsdownload';

import 'video.js/dist/video-js.css';
import 'videojs-vjsdownload/dist/videojs-vjsdownload.css';
import 'videojs-seek-buttons/dist/videojs-seek-buttons.css';
import './styles.css';
import '../video-marker-unmoderated/index';
import '../video-marker-unmoderated/styles.css';

import {
  useMedia, Media, MediaContext, withMedia, captureException,
} from '@playbookux/playbook-web-core';

export { withMedia, useMedia, Media };

const VideoPlayer = ({
  downloadLink,
  src,
  children,
  markers,
  autoplay,
  onEnded,
  onError,
}) => {
  const options = {
    playbackRates: [1.0, 1.5, 2.0],
    plugins: {
      seekButtons: {
        forward: 10,
        back: 10,
      },
      vjsdownload: {
        beforeElement: 'playbackRateMenuButton',
        textControl: 'Download video',
        name: 'downloadButton',
        downloadURL: downloadLink || src,
      },
      markers: {
        ...markers,
      },
    },
    controls: true,
    autoplay: autoplay || false,
    fill: true,
    fluid: false,
    preload: 'auto',
    html5: {
      hls: {
        enableLowInitialPlaylist: true,
        smoothQualityChange: true,
        overrideNative: false,
      },
      vhs: {
        overrideNative: false,
      },
    },
    clipButton: {
      beforeElement: 'downloadButton',
      textControl: 'Create clip',
      name: 'clipButton',
    },
  };

  const {
    player,
    setPlayer,
    setPlayerState,
    setPlayerSetters,
  } = useContext(MediaContext);

  const videoRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [playerReady, setPlayerReady] = useState(false);
  const [loadedData, setLoadedData] = useState(false);
  const [playerRect, setPlayerRect] = useState({ height: 0, width: 0 });

  useEffect(() => {
    const vjsPlayer = videojs(videoRef.current, {
      ...options,
      inactivityTimeout: 0,
      sources: [src],
    });

    setPlayer(vjsPlayer);

    setPlayerSetters({
      seekTo: (time) => vjsPlayer.currentTime(time),
      pause: () => {
        vjsPlayer.pause();
      },
      play: () => {
        vjsPlayer.play();
      },
      getDuration: () => vjsPlayer.duration(),
    });

    vjsPlayer.on('loadedmetadata', () => {
      vjsPlayer.hasStarted(true);
    });

    vjsPlayer.on('loadeddata', () => {
      vjsPlayer.hasStarted(true);
      setLoadedData(true);
    });

    vjsPlayer.on('timeupdate', () => {
      setCurrentTime(vjsPlayer.currentTime());
    });

    vjsPlayer.on('playerresize', () => {
      const ctrlBar = vjsPlayer.el().getBoundingClientRect();
      setPlayerRect({ height: ctrlBar.height, width: ctrlBar.width });
    });

    vjsPlayer.on('ready', () => {
      setPlayerReady(true);
    });

    /** ---- Handle playback error ---- */
    vjsPlayer.on('error', () => {
      const error = vjsPlayer.error();
      captureException('VIDEO_PLAYER_ERROR', error);
      if (onError) {
        onError(error);
      }
    });

    vjsPlayer.on('ended', () => {
      if (onEnded) {
        onEnded();
      }
    });

    return () => {
      if (player !== null) {
        player.markers?.destroy();
        player.dispose();
      }
    };
  }, []);

  useEffect(() => {
    setPlayerState({
      currentTime,
      playerReady,
      loadedData,
      playerRect,
    });
  }, [currentTime, loadedData, playerReady, playerRect, setPlayerState]);

  useEffect(() => {
    if (player !== null) {
      player.src({ src });
    }
  }, [src, player]);

  return (
    <div data-vjs-player>
      <video ref={videoRef} className="video-js" />
      {playerReady && children}
    </div>
  );
};

VideoPlayer.defaultProps = {
  downloadLink: '',
  markers: {},
  autoplay: false,
  onEnded: null,
  onError: null,
  children: null,
};

VideoPlayer.propTypes = {
  downloadLink: string,
  src: string.isRequired,
  children: oneOfType([
    arrayOf(node),
    node,
  ]),
  markers: shape(shape({})),
  autoplay: bool,
  onEnded: func,
  onError: func,
};

VideoPlayer.defaultProps = {
  children: null,
  markers: {},
  autoplay: false,
  onEnded: null,
};

export default VideoPlayer;
